import React, { useState } from "react";
import { acceptedIcon, downArrow, leftArrow, NAIcon, rejectedIcon, rightArrow, startedIcon, upArrow, viewIcon } from "../../icons";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const HandoverChecklistDetailModal = ({
  handleClose,
  show,
  handoverId,
  handoverData,
}) => {
  const [expandedDateGroups, setExpandedDateGroups] = useState({});
  const [expanded, setExpanded] = useState({});
  const [fullscreenImages, setFullscreenImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  
  const statusColor = [
    { status: "accepted", color: '#90EE90' },
    { status: "rejected", color: '#FF5653' },
    { status: "N/A", color: '#0101012a' },
    { status: "partially accepted", color: "#90EE90"},
  ];
  const showHideClassName = show ? "user_detail_modal_show" : "";
  const handoverItem = handoverData?.filter((item) => item.id === handoverId);
  // Separate handovers into partial submissions and remaining items
  const separateHandovers = (handovers) => {
    // --- helpers for parent + child ---
  
    const hasPartialInChecklist = (cl) => {
      const isPartialSelf =
        cl.partial_submission_id !== null && cl.partialSubmission !== null;
  
      const hasPartialChild = cl.child?.some(
        (c) =>
          c.partial_submission_id !== null && c.partialSubmission !== null
      );
  
      return isPartialSelf || hasPartialChild;
    };
  
    const hasRemainingInChecklist = (cl) => {
      const isRemainingSelf = cl.partial_submission_id === null;
  
      const hasRemainingChild = cl.child?.some(
        (c) => c.partial_submission_id === null
      );
  
      return isRemainingSelf || hasRemainingChild;
    };
  
    const getPartialChecklist = (cl) => {
      const isPartialSelf =
        cl.partial_submission_id !== null && cl.partialSubmission !== null;
  
      const partialChildren =
        cl.child?.filter(
          (c) =>
            c.partial_submission_id !== null && c.partialSubmission !== null
        ) || [];
  
      // jo na self partial che, na child partial che → null (ignore)
      if (!isPartialSelf && partialChildren.length === 0) return null;
  
      return {
        ...cl,
        // child ma only partial items
        ...(partialChildren.length > 0 ? { child: partialChildren } : {}),
      };
    };
  
    const getRemainingChecklist = (cl) => {
      const isRemainingSelf = cl.partial_submission_id === null;
  
      const remainingChildren =
        cl.child?.filter((c) => c.partial_submission_id === null) || [];
  
      // jo na self remaining che, na child remaining che → null (ignore)
      if (!isRemainingSelf && remainingChildren.length === 0) return null;
  
      return {
        ...cl,
        ...(remainingChildren.length > 0 ? { child: remainingChildren } : {}),
      };
    };
  
    // --- main logic ---
  
    const partialSubmissions = [];
    const remainingItems = [];
  
    handovers?.forEach((handover) => {
      const checkLists = handover.checkLists || [];
  
      const hasPartialSubmissions = checkLists.some((cl) =>
        hasPartialInChecklist(cl)
      );
  
      const hasRemainingItems = checkLists.some((cl) =>
        hasRemainingInChecklist(cl)
      );
  
      if (hasPartialSubmissions) {
        const partialChecklists = checkLists
          .map(getPartialChecklist)
          .filter(Boolean); // null hata te hatai jase
  
        partialSubmissions.push({
          ...handover,
          checkLists: partialChecklists,
        });
      }
  
      if (hasRemainingItems) {
        const remainingChecklists = checkLists
          .map(getRemainingChecklist)
          .filter(Boolean);
  
        remainingItems.push({
          ...handover,
          checkLists: remainingChecklists,
        });
      }
    });
  
    // --- handover_status update for partialSubmissions ---
  
    const remainingMap = new Map(remainingItems.map((h) => [h.id, h]));
  
    const updatedPartialSubmissions = partialSubmissions.map((partial) => {
      const remaining = remainingMap.get(partial.id);
      const hasRemainingChecklists = remaining?.checkLists?.length > 0;
  
      const newStatus = hasRemainingChecklists
        ? "PARTIALLY ACCEPTED"
        : "ACCEPTED";
  
      return {
        ...partial,
        handover_status: newStatus,
      };
    });
  
    return { partialSubmissions: updatedPartialSubmissions, remainingItems };
  };

  // Toggle a date-group open/closed
  const toggleDateGroup = (date, times = null) => {
    const isCurrentlyExpanded = !!expandedDateGroups[date];
    const nextExpanded = !isCurrentlyExpanded;
  
    setExpandedDateGroups((prev) => ({
      ...prev,
      [date]: nextExpanded,
    }));
  
    setExpanded((prev) => {
      const updated = { ...prev };
      const datePrefix = `${date}-`;
      const partialPrefix = `partial-${date}-`;
  
      Object.keys(updated).forEach((key) => {
        if (key.startsWith(datePrefix) || key.startsWith(partialPrefix)) {
          delete updated[key];
        }
      });
  
      if (nextExpanded && times) {
        const [firstTime] = Object.keys(times);
        if (firstTime) {
          const timeKey = `${date}-${firstTime}`;
          updated[timeKey] = true; // open the first "Submitted at"
        }
      }
  
      return updated;
    });
  };
  
  const getPartialSubmissionCreatedAt = (handoverItems) => {
    const checkLists = handoverItems?.checkLists || [];
  
    for (const cl of checkLists) {
      // 1) parent-level partialSubmission
      if (cl?.partialSubmission?.createdAt) {
        return cl.partialSubmission.createdAt;
      }
  
      // 2) child-level partialSubmission
      const children = cl?.child || [];
      for (const ch of children) {
        if (ch?.partialSubmission?.createdAt) {
          return ch.partialSubmission.createdAt;
        }
      }
    }
  
    // joiye to kahi pan createdAt nathi
    return null;
  };
  
  const groupByPartialSubmissionDateTime = (submissions) => {
    return submissions.reduce((groups, handoverItems) => {
      const createdAt = getPartialSubmissionCreatedAt(handoverItems);
  
      if (!createdAt) {
        if (!groups["Unknown date"]) groups["Unknown date"] = {};
        if (!groups["Unknown date"]["Unknown time"])
          groups["Unknown date"]["Unknown time"] = [];
  
        groups["Unknown date"]["Unknown time"].push(handoverItems);
        return groups;
      }
  
      const dateKey = format(new Date(createdAt), "dd/MM/yyyy");
      const timeKey = format(new Date(createdAt), "hh:mm a"); // time grouping
  
      if (!groups[dateKey]) groups[dateKey] = {};
      if (!groups[dateKey][timeKey]) groups[dateKey][timeKey] = [];
  
      groups[dateKey][timeKey].push(handoverItems);
  
      return groups;
    }, {});
  };
  const navigate = useNavigate();

  const toggleExpand = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleWheelZoom = (event) => {
    setZoom((prev) => (event.deltaY < 0 ? Math.min(prev + 0.2, 3) : Math.max(prev - 0.2, 1)));
  };

  const openImageViewer = (images, startIndex = 0) => { // Added startIndex
    if (images?.length > 0) {
      setFullscreenImages(images);
      setCurrentImageIndex(startIndex); // Set the index to the clicked index
    }
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    setCurrentImageIndex((prev) => (prev + 1) % fullscreenImages.length);
  };

  const handlePrevImage = (e) => {
    e.preventDefault();
    setCurrentImageIndex((prev) => (prev - 1 + fullscreenImages.length) % fullscreenImages.length);
  };

  const isVideo = (file) => {
    return file?.toLowerCase().endsWith(".mp4") ||
      file?.toLowerCase().endsWith(".webm") ||
      file?.toLowerCase().endsWith(".ogg") ||
      file?.toLowerCase().endsWith(".mov");
  };
  const renderRemarkMedia = (remarkFiles, startIndex) => {
    if (!remarkFiles || remarkFiles.length === 0) {
        return null;
    }

    return (
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap"}}>
            {remarkFiles.map((fileName, i) => {
                const fullPath = `${process.env.REACT_APP_FILE_BASE_URL}/${fileName}`;
                
                if (isVideo(fileName)) {
                    // Render Video
                    return (
                        <div key={i} style={{ width: 100, height: 100, position: 'relative' }}>
                            <video
                                width="100"
                                height="100"
                                autoPlay
                                playsInline
                                loop
                                muted
                                controls
                                style={{ objectFit: "cover", borderRadius: "4px" }}
                                onClick={() => openImageViewer(remarkFiles, i)}
                            >
                                <source src={fullPath} type={`video/${fileName.split('.').pop()}`} />
                            </video>
                        </div>
                    );
                } else {
                    // Render Image
                    return (
                        <img
                            key={i}
                            src={fullPath}
                            alt={`partial-remark-${i}`}
                            width={100}
                            height={100}
                            style={{ objectFit: "cover", borderRadius: "4px" }}
                            onClick={() => openImageViewer(remarkFiles, i)}
                        />
                    );
                }
            })}
        </div>
    );
  };

  // Render handover item component (modified to optionally hide submission info)
  const renderHandoverItem = (handoveritems, index, type, showSubmissionInfo = true) => {
    const expandKey = `${type}-${index}`;
    
    return (
      <div
        key={index}
        className="inquiry_complaint_detail_text handover-item"
        style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor:
            handoveritems.handover_status?.toLowerCase() === "rejected"
              ? "#ffdddd"
              : "#f9f9f9",
          padding: "15px",
          borderRadius: "8px",
          marginBottom: "10px",
          boxShadow: type === "partial" ? "0px 2px 5px rgba(0, 0, 0, 0.4)" : "0px 2px 5px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Header with title and status */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            fontWeight: "600",
          }}
          onClick={() => toggleExpand(expandKey)}
        >
          <h3 className="inquiry_complaint_detail_heading">
            {handoveritems.handover_title}
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontWeight: "bold",
                color:
                  handoveritems.handover_status?.toLowerCase() === "rejected"
                    ? "#c11f3b"
                    : "#5cb85c",
              }}
            >
              {handoveritems.handover_status}
            </span>
            {expanded[expandKey] ? upArrow : downArrow}
          </div>
        </div>

        {expanded[expandKey] && (
          <div style={{ marginTop: "10px" }}>
            {/* Partial Submission Info (only for partial submissions and if showSubmissionInfo is true) */}
            {type === 'partial' && showSubmissionInfo && handoveritems.checkLists[0]?.partialSubmission && (
              <div
                style={{
                  marginBottom: "15px",
                  backgroundColor: handoveritems.handover_status?.toLowerCase() === "rejected"
                  ? "#ffdddd"
                  : "#f9f9f9",
                }}
              >
                <h4 className="inquiry_complaint_detail_heading">
                  Submission Info
                </h4>
                <div className="inquiry_complaint_detail_text" style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "10px 0"
                }}>
                  <div className=""
                    style={{
                      display: "flex",
                      gap: "35px",
                    }}>
                    <p className="inquiry_complaint_detail_heading">
                      Witness:
                      <p style={{ color: "black", fontSize: "16px", fontWeight: "normal" }}>
                        {handoveritems.checkLists[0].partialSubmission.handover_wtname} {handoveritems.checkLists[0].partialSubmission.handover_wtnumber}
                      </p>
                    </p>
                    <p className="inquiry_complaint_detail_heading">
                      Remark:
                      <p style={{ color: "black", fontSize: "16px", fontWeight: "normal" }}>
                        {handoveritems.checkLists[0].partialSubmission.handover_remark || "No remark provided"}
                      </p>
                    </p>
                    {handoveritems.checkLists[0].partialSubmission.hand_remark_images && 
                      (() => {
                        let remarkFiles = [];
                        try {
                            remarkFiles = JSON.parse(handoveritems.checkLists[0].partialSubmission.hand_remark_images);
                        } catch (e) {
                            console.error("Error parsing remark images JSON:", e);
                        }
                        return renderRemarkMedia(remarkFiles, 0);
                     })()
                    }
                  </div>
                  {handoveritems.checkLists[0].partialSubmission.handover_signature ? (
                    <div>
                      <p className="inquiry_complaint_detail_heading">Signature:</p>
                      <br />
                      <img
                        src={`${process.env.REACT_APP_FILE_BASE_URL}/${handoveritems.checkLists[0].partialSubmission.handover_signature}`}
                        alt="partial-signature"
                        width={200}
                        height={80}
                      />
                    </div>
                  ) : (
                    <p>No signature available</p>
                  )}
                </div>
              </div>
            )}

            {/* Checklists and Rejection Details */}
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "space-between",
                transition: "max-height 0.3s ease-in-out",
              }}
            >
              <div style={{ width: "100%" }}>
                <h3 className="inquiry_complaint_detail_heading">
                  Checklists
                </h3>
                <ul
                  className="inquiry_complaint_detail_name inquiry_complaint_detail_ul"
                  style={{
                    textAlign: "start",
                    fontWeight: "bold",
                    lineHeight: "1.5",
                  }}
                >
                  {handoveritems.checkLists?.map((checklist, checklistIndex) =>{
                    const hasChildren = checklist.child?.length > 0;

                    // 🔍 Check if ALL child status = "N/A"
                    const allChildNA = hasChildren
                      ? checklist.child.every((subitem) => subitem?.status === "N/A")
                      : false;
                    
                    return hasChildren ? (
                      <li key={`${index}-${checklistIndex}`} style={{ marginBottom: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div className="checklist_child" style={{ borderColor: statusColor.find(item => item.status?.toLowerCase() === checklist?.status?.toLowerCase())?.color }}>
                          {checklist?.title}
                        </div>
                        {allChildNA && <div className="NA_Btn">N/A</div>}
                          </div>
                        <ol
                          type="i"  
                          style={{
                            marginTop: "0px",
                            marginLeft: "30px",
                            lineHeight: "1.25",
                            fontWeight: "normal",
                          }}
                        >
                          {checklist.child.map((subitem, subitemIndex) => (
                            <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
                            <li
                              key={`${checklistIndex}-${subitemIndex}`}
                              style={{
                                borderColor: statusColor.find(item => item.status?.toLowerCase() === subitem?.status?.toLowerCase())?.color,
                                paddingLeft: "10px",
                              }}
                            >
                              <span>{subitem.title}</span>
                            </li>
                            {!allChildNA && subitem?.status === "N/A" && (
                              <div className="NA_Btn">N/A</div>
                            )}
                            </div>
                          ))}
                        </ol>
                      </li>
                    ) : (
                      <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
                      <li
                        key={checklistIndex}
                        style={{ marginBottom: "8px" }}
                      >
                        <div className="checklist_child" style={{ borderColor: statusColor.find(item => item.status?.toLowerCase() === checklist?.status?.toLowerCase())?.color }}>
                          {checklist?.title}
                        </div>
                      </li>
                        {
                          checklist?.status === "N/A" && 
                            <div className="NA_Btn">N/A</div>
                        }
                      </div>
                    )
                  }
                  )}
                </ul>
              </div>

              <div style={{ width: "100%" }}>
                {handoveritems?.handover_status?.toLowerCase() === "rejected" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div>
                      <h4 className="inquiry_complaint_detail_heading">
                        Reason
                      </h4>
                      <p
                        className="inquiry_complaint_detail_name"
                        style={{ textAlign: "start" }}
                      >
                        {handoveritems?.rejected_reason}
                      </p>
                    </div>

                    {handoveritems?.rejected_attachment && (
                      <div>
                        <h4
                          className="inquiry_complaint_detail_heading"
                          onClick={() => navigate(`/complaints/page/${1}`)}
                        >
                          View Attachment
                        </h4>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  function renderPartialSubmissions({
    partialSubmissions,
    expandedDateGroups,
    toggleDateGroup,
    renderHandoverItem,
    upArrow,
    downArrow,
  }) {
    const grouped = groupByPartialSubmissionDateTime(partialSubmissions);

return Object.entries(grouped).map(([date, times]) => (
  <div key={date} style={{ marginBottom: 16 }}>
    
    <h2
      onClick={() => toggleDateGroup(date, times)}
      style={{
        marginBottom: "5px",
        borderBottom: "2px solid #ddd",
        padding: "5px 0",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      Submission on {date}
      {expandedDateGroups[date] ? upArrow : downArrow}
    </h2>

    {expandedDateGroups[date] &&
      Object.entries(times).map(([time, submissions]) => {
        if (submissions.length === 0) return null;

        // Assume all submissions in this group share the same partialSubmission (based on grouping)
        const partial = submissions[0]?.checkLists[0]?.partialSubmission;
        const timeKey = `${date}-${time}`;

        return (
          <div key={time} style={{marginBottom: 8 }}>
            <div
              onClick={() => toggleExpand(timeKey)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontWeight: "600",
                padding: "10px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
              }}
            >
              <h3 style={{ margin: 0 }}>
                Submitted at {time}{partial?.handover_wtname ? ` by ${partial.handover_wtname}.` : ""}
              </h3>
              {expanded[timeKey] ? upArrow : downArrow}
            </div>

            {expanded[timeKey] && partial && (
              <>
                {/* Render shared Partial Submission Info */}
                <div
                  style={{
                    marginBottom: "15px",
                    backgroundColor:
                      partial.handover_status?.toLowerCase() === "rejected"
                        ? "#ffdddd"
                        : "#f9f9f9",
                    padding: "10px",
                    borderRadius: "4px",
                  }}
                >
                  <h4 className="inquiry_complaint_detail_heading">
                    Submission Info
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      padding: "10px 0",
                    }}
                  >
                    <div style={{ display: "flex", gap: "35px" }}>
                      <p className="inquiry_complaint_detail_heading">
                        Witness:
                        <p style={{ color: "black", fontSize: "16px", fontWeight: "normal" }}>
                          {partial.handover_wtname} {partial.handover_wtnumber}
                        </p>
                      </p>
                      <p className="inquiry_complaint_detail_heading">
                        Remark:
                        <p style={{ color: "black", fontSize: "16px", fontWeight: "normal" }}>
                          {partial.handover_remark || "No remark provided"}
                        </p>
                      </p>
                      {partial.hand_remark_images &&
                        (() => {
                          let remarkFiles = [];
                          try {
                            remarkFiles = JSON.parse(partial.hand_remark_images);
                          } catch (e) {
                            console.error("Error parsing remark images JSON:", e);
                          }
                          return renderRemarkMedia(remarkFiles, 0);
                        })()}
                    </div>
                    {partial.handover_signature ? (
                      <div style={{ marginTop: "8px" }}>
                        <p className="inquiry_complaint_detail_heading">Signature:</p>
                        <br />
                        <img
                          src={`${process.env.REACT_APP_FILE_BASE_URL}/${partial.handover_signature}`}
                          alt="partial-signature"
                          width={200}
                          height={80}
                        />
                      </div>
                    ) : (
                      <p>No signature available</p>
                    )}
                  </div>
                {/* Render the handover items (checklists) without repeating submission info */}
                {submissions.map((handoveritems, idx) =>
                  renderHandoverItem(handoveritems, `${timeKey}-${idx}`, "partial", false)
                )}
                </div>

              </>
            )}
          </div>
        );
      })}
  </div>
));
  }

  return (
    <>
      <div
        className={`user_detail_modal_bg ${showHideClassName}`}
        onClick={handleClose}
      ></div>
      <section
        className={`user_detail_modal_main ${showHideClassName}`}
        style={{
          borderRadius: "10px",
          padding: "20px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          backgroundColor: "#fff",
        }}
      >
        <div className="inquiry_complaint_detail_section">
          <div className="inquiry_complaint_detail_content">
            <div className="inquiry_complaint_detail_body">
              {/* <h2
                style={{
                  marginBottom: "5px",
                  borderBottom: "2px solid #ddd",
                  paddingBottom: "5px",
                }}
              >
                Possession Details
              </h2> */}

              {handoverItem?.map((item, index) => {
                const { partialSubmissions, remainingItems } = separateHandovers(item.handovers);
                
                return (
                  <div key={index}>
                    {/* Partial Submissions Section */}
                    {partialSubmissions.length > 0 &&
                      <>
                        <h2
                          style={{
                            marginBottom: "5px",
                            borderBottom: "2px solid #ddd",
                            paddingBottom: "5px",
                          }}
                        >
                          Submitted possession details
                        </h2>
                        {renderPartialSubmissions({
                          partialSubmissions,
                          expandedDateGroups,
                          toggleDateGroup,
                          renderHandoverItem,
                          upArrow,
                          downArrow,
                        })}
                    </>}

                    {/* Remaining Items Section */}
                    {remainingItems.length > 0 && (
                      <>
                        <h2
                          style={{
                            marginBottom: "5px",
                            borderBottom: "2px solid #ddd",
                            paddingBottom: "5px",
                          }}
                        >
                          Remaining possession details
                        </h2>
                        {remainingItems.map((handoveritems, handoverIndex) => 
                          renderHandoverItem(handoveritems, handoverIndex, 'remaining')
                        )}
                      </>
                    )}

                    {/* No data message */}
                    {partialSubmissions.length === 0 && remainingItems.length === 0 && (
                      <div style={{ fontSize: "15px", fontWeight: "600", textAlign: "center", marginTop: "20px" }}>
                        No Possession Data found
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="user_detail_modal_close_btn complaint_view_modalbtn comman_btn"
            style={{
              padding: "10px 20px",
              backgroundColor: "#C11F3B",
              color: "#fff",
              borderRadius: "5px",
              fontWeight: "bold",
              cursor: "pointer",
              marginTop: "20px",
            }}
          >
            Close
          </button>
        </div>
      </section>

      {/* Fullscreen image viewer (unchanged) */}
      {fullscreenImages.length > 0 && (
        <div
          className="fullscreen-modal"
          onWheel={handleWheelZoom}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            zIndex: 999999,
          }}
        >
          <button
            className="nav-button left"
            style={{
              position: "absolute",
              left: "100px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255, 255, 255, 0.5)",
              border: "none",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              zIndex: "1",
              fontSize: "24px",
              color: "#333",
              boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => handlePrevImage(e)}
          >
            {leftArrow}
          </button>

          {isVideo(fullscreenImages[currentImageIndex]) ? (
            <video
              controls
              style={{ maxWidth: "90%", maxHeight: "90%" }}
            >
              <source
                src={`${process.env.REACT_APP_FILE_BASE_URL}/${fullscreenImages[currentImageIndex]}`}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={`${process.env.REACT_APP_FILE_BASE_URL}/${fullscreenImages[currentImageIndex]}`}
              alt="Fullscreen Preview"
              style={{
                transform: `scale(${zoom})`,
                transition: "transform 0.2s ease-in-out",
                maxWidth: "90%",
                maxHeight: "90%",
              }}
            />
          )}

          <button
            className="nav-button right"
            style={{
              position: "absolute",
              right: "100px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255, 255, 255, 0.5)",
              border: "none",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "24px",
              color: "#333",
              boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => handleNextImage(e)}
          >
            {rightArrow}
          </button>

          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button
              onClick={() => setFullscreenImages([])}
              style={{
                padding: "10px",
                backgroundColor: "#fff",
                color: "#333",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontWeight: "bold",
                boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HandoverChecklistDetailModal;